import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Loader2, LogOut, TrendingUp, Trophy, CheckCircle2, Circle, Calendar, DollarSign,
  Paperclip, FileText, Activity, ArrowLeft, Sparkles, ExternalLink, ClipboardList,
  ShieldCheck, Landmark, Lock, RefreshCw, X, ArrowRightLeft, Trash2, Clock,
  Check, Download, FolderOpen, Eye, Mail, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { usePortalAuth } from "../context/PortalAuth";
import AchAuthorizationCard from "../components/site/AchAuthorizationCard";
import EngagementAuthorizationCard from "../components/site/EngagementAuthorizationCard";
import AdvisorMeetingCard from "../components/site/AdvisorMeetingCard";
import ReferBusinessCard from "../components/site/ReferBusinessCard";
import PortalFinances from "../components/site/PortalFinances";
import PortalCRM from "../components/site/PortalCRM";
import PortalTeamAccess from "../components/site/PortalTeamAccess";
import PortalDocuments from "../components/site/PortalDocuments";
import PortalOperations from "../components/site/PortalOperations";
import PortalRecommendations from "../components/site/PortalRecommendations";
import HandbookBoard from "../components/site/HandbookBoard";
import PortalSupport from "../components/site/PortalSupport";
import Workspace from "../components/site/Workspace";
import RecoveryStep from "../components/site/RecoveryStep";

const money = (n) => `$${Math.round(n || 0).toLocaleString()}`;

function HealthRingCard({ score, scoredAt }) {
  const has = score != null;
  const pct = has ? Math.max(0, Math.min(100, score)) : 0;
  const [fill, setFill] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setFill(pct), 150);
    return () => clearTimeout(t);
  }, [pct]);
  const color = pct >= 70 ? "#6b7c3a" : pct >= 50 ? "#c9a961" : "#b23a48";
  const R = 30;
  const C = 2 * Math.PI * R;
  return (
    <div data-testid="stat-health" className="rounded-2xl border border-[#c9a961]/40 bg-[#c9a961]/10 text-[#1e3d3a] p-5">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] opacity-70">
        <TrendingUp className="h-3.5 w-3.5" strokeWidth={1.8} />
        Business health score
      </div>
      <div className="mt-2 flex items-center gap-4">
        <div className="relative h-[72px] w-[72px] shrink-0">
          <svg width="72" height="72" viewBox="0 0 72 72" className="-rotate-90">
            <circle cx="36" cy="36" r={R} fill="none" stroke="#1e3d3a" strokeOpacity="0.12" strokeWidth="7" />
            {has && (
              <circle
                cx="36" cy="36" r={R} fill="none"
                stroke={color} strokeWidth="7" strokeLinecap="round"
                strokeDasharray={C} strokeDashoffset={C - (C * fill) / 100}
                style={{ transition: "stroke-dashoffset 1.2s cubic-bezier(0.16,1,0.3,1), stroke 0.6s ease" }}
              />
            )}
          </svg>
          <div data-testid="stat-health-value" className="absolute inset-0 flex items-center justify-center font-head text-xl tabular-nums" style={{ color: has ? color : undefined }}>
            {has ? pct : "—"}
          </div>
        </div>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.2em] opacity-60">out of 100</div>
          <div className="mt-1 text-[11px] opacity-55">
            {scoredAt
              ? `Elevare score · ${new Date(scoredAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
              : "Waiting on first analysis"}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, hint, testid, tone = "ink" }) {
  const styles = {
    ink: "border-[#1e3d3a]/12 bg-white text-[#1e3d3a]",
    gold: "border-[#c9a961]/40 bg-[#c9a961]/10 text-[#1e3d3a]",
    forest: "border-[#1e3d3a]/12 bg-[#1e3d3a] text-white",
  };
  return (
    <div data-testid={testid} className={`rounded-2xl border ${styles[tone]} p-5 space-y-1`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] opacity-70">
        <Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
        {label}
      </div>
      <div className="font-head text-3xl tabular-nums">{value}</div>
      {hint && <div className="text-[11px] opacity-55">{hint}</div>}
    </div>
  );
}

export default function PortalDashboard() {
  const { user, logout } = usePortalAuth();
  const [data, setData] = useState(null);
  const [finTab, setFinTab] = useState("progress");
  const [tabAutoSet, setTabAutoSet] = useState(false);
  const [access, setAccess] = useState(null);
  const [plan, setPlan] = useState(null);
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const [identityLoading, setIdentityLoading] = useState(false);
  const [bankingLoading, setBankingLoading] = useState(false);
  const [refreshingBalances, setRefreshingBalances] = useState(false);
  const [history, setHistory] = useState({}); // fc_account_id -> [{day,available_cents}...]
  const [transfers, setTransfers] = useState([]);
  const [showTransfer, setShowTransfer] = useState(false);
  const [removingPm, setRemovingPm] = useState(null);
  const [preview, setPreview] = useState(null); // {url, mime, filename} for inline doc preview
  const [emailModal, setEmailModal] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [curPw, setCurPw] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const nav = useNavigate();

  const changeEmail = async () => {
    const nw = newEmail.trim().toLowerCase();
    if (!nw || nw === (data?.user?.email || "").toLowerCase()) { toast.error("Enter a new, different email."); return; }
    setEmailBusy(true);
    try {
      const { data: res } = await api.post("/portal/auth/change-email", { new_email: nw, current_password: curPw || undefined });
      toast.success(`Email updated across ${res.records_updated} record${res.records_updated === 1 ? "" : "s"}.`);
      setEmailModal(false); setNewEmail(""); setCurPw("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change email.");
    } finally {
      setEmailBusy(false);
    }
  };

  const load = () => {
    api.get("/portal/summary")
      .then(({ data }) => {
        // First-time users are routed through the welcome/onboarding page.
        if (data?.user && !data.user.has_seen_tour) {
          nav("/portal/welcome", { replace: true });
          return;
        }
        setData(data);
      })
      .catch(() => toast.error("Could not load your portal."));
    api.get("/portal/recovery-plan")
      .then(({ data }) => setPlan(data?.plan || null))
      .catch(() => { /* silent — no plan yet is normal */ });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  // Self-service clients land on Business Finances (their expense-tracking home).
  useEffect(() => {
    if (data?.user?.self_service && !tabAutoSet) {
      setFinTab("finances");
      setTabAutoSet(true);
    }
  }, [data, tabAutoSet]);
  useEffect(() => {
    api.get("/portal/access").then(({ data }) => setAccess(data)).catch(() => setAccess({ locked: false }));
  }, []);

  // Lightweight polling so approval-status pills update within ~20s of an
  // admin's review, without needing websockets or SSE. Only runs while the
  // tab is visible so we don't waste bandwidth on backgrounded portals.
  useEffect(() => {
    const tick = () => { if (document.visibilityState === "visible") load(); };
    const id = setInterval(tick, 20000);
    // When the client returns to this tab (e.g. back from the Stripe Identity
    // flow), force a live Stripe re-check then reload so the card auto-updates
    // to "Verified" or "Try again" without a manual refresh.
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        api.get("/portal/identity/status").catch(() => {}).finally(load);
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line
  }, []);

  // While Stripe is reviewing submitted documents ("processing"), auto-poll the
  // live status every 5s so the card flips to Verified on its own.
  const idStatus = data?.identity?.status;
  useEffect(() => {
    if (idStatus !== "processing") return;
    const id = setInterval(() => {
      api.get("/portal/identity/status").catch(() => {}).finally(load);
    }, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [idStatus]);

  // Fetch history + transfers whenever banks change (fresh render after
  // linking/removing accounts, plus the initial mount once banks arrive).
  const bankCount = data?.identity?.bank_accounts?.length || 0;
  useEffect(() => {
    if (bankCount === 0) return;
    api.get("/portal/banking/balance/history?days=30")
      .then(({ data: h }) => setHistory(h?.history || {}))
      .catch(() => {});
    api.get("/portal/banking/transfers")
      .then(({ data: t }) => setTransfers(t?.transfers || []))
      .catch(() => {});
  }, [bankCount]);

  const upload = async (taskIdx, file) => {
    if (!file) return;
    setUploadingIdx(taskIdx);
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(`/portal/progress/task/${taskIdx}/attach`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${file.name} attached.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploadingIdx(null);
    }
  };

  // Preview a PDF or image inline (modal) before the client downloads it.
  const canPreview = (att) => {
    const m = (att?.mime || "").toLowerCase();
    const f = (att?.filename || "").toLowerCase();
    return m.startsWith("image/") || m === "application/pdf" ||
      /\.(png|jpe?g|gif|webp|bmp|pdf)$/.test(f);
  };
  const previewAttachment = async (taskIdx, att) => {
    try {
      const res = await api.get(
        `/portal/progress/task/${taskIdx}/attachment/${att.id}/download`,
        { responseType: "blob" },
      );
      const mime = att.mime || res.data.type || "application/octet-stream";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      setPreview({ url, mime, filename: att.filename || "document" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open preview.");
    }
  };
  const closePreview = () => {
    if (preview?.url) window.URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const [welcomeDl, setWelcomeDl] = useState(false);
  const downloadWelcomeGuide = async () => {
    setWelcomeDl(true);
    try {
      const res = await api.get("/portal/handbook/pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "Elevare-Welcome-Guide.pdf"; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 3000);
    } catch { toast.error("Couldn't generate your welcome guide."); }
    finally { setWelcomeDl(false); }
  };

  // Fetch the client's uploaded doc as a blob and trigger a browser save so
  // they can reference or re-download anything they've sent us.
  const downloadAttachment = async (taskIdx, att) => {
    try {
      const res = await api.get(
        `/portal/progress/task/${taskIdx}/attachment/${att.id}/download`,
        { responseType: "blob" },
      );
      const blob = new Blob([res.data], { type: att.mime || "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = att.filename || "document";
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        link.remove();
        window.URL.revokeObjectURL(url);
      }, 800);
    } catch (e) {
      // Download is gated — a founder must approve first.
      if (e?.response?.status === 403) {
        try {
          const { data } = await api.post("/portal/download-requests", {
            file_id: att.id, task_index: taskIdx, label: att.filename || "Document",
          });
          if (data.status === "approved") toast.message("Approved — tap download again to save your file.");
          else toast.success("Download requested — John or Kalib will review it shortly.");
        } catch {
          toast.error("Could not request approval. Please try again.");
        }
        return;
      }
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Download failed.");
    }
  };

  // Let the client remove one of their own uploads before it's been
  // approved. Blocked server-side once a founder has stamped `approved` on
  // the attachment. After success we optimistically re-fetch progress so
  // the row disappears immediately.
  const removeAttachment = async (taskIdx, att) => {
    if (att.review_status === "approved") {
      toast.error("This document has been approved — reach out to Kalib or John to swap it out.");
      return;
    }
    const label = att.filename || "this file";
    if (!window.confirm(`Remove "${label}"? You can re-upload a new version right after.`)) return;
    try {
      await api.delete(`/portal/progress/task/${taskIdx}/attachment/${att.id}`);
      toast.success("Removed. Upload a new version whenever you're ready.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove the document.");
    }
  };

  const signOut = async () => {
    await logout();
    nav("/portal/login", { replace: true });
  };

  const startIdentity = async () => {
    // Open the tab synchronously inside the click gesture — if we wait until
    // after the await, browsers treat it as a non-user action and block it,
    // which is why verification wouldn't open.
    const win = window.open("about:blank", "_blank");
    setIdentityLoading(true);
    try {
      const { data: res } = await api.post("/portal/identity/start");
      if (res?.url) {
        if (win && !win.closed) {
          win.location.href = res.url;
          toast.success("Opening Stripe Identity…");
        } else {
          // Popup blocked — send them there in the same tab (Stripe returns them to the portal).
          window.location.href = res.url;
        }
        setTimeout(load, 15000);
      } else {
        if (win && !win.closed) win.close();
        toast.error("Stripe returned no URL. Try again in a minute.");
      }
    } catch (e) {
      if (win && !win.closed) win.close();
      toast.error(e?.response?.data?.detail || "Could not start verification.");
    } finally {
      setIdentityLoading(false);
    }
  };

  const refreshIdentity = async () => {
    try {
      await api.get("/portal/identity/status");
      load();
      toast.success("Status refreshed.");
    } catch {
      toast.error("Could not refresh status.");
    }
  };

  const refreshBalances = async () => {
    setRefreshingBalances(true);
    try {
      const { data: res } = await api.post("/portal/banking/balance/refresh");
      const withBalance = (res?.results || []).filter((r) => r.balance && !r.balance.pending);
      const pending = (res?.results || []).filter((r) => r.balance && r.balance.pending);
      if (withBalance.length > 0) {
        toast.success(`Balances refreshed for ${withBalance.length} account${withBalance.length === 1 ? "" : "s"}.`);
      } else if (pending.length > 0) {
        toast.info("Stripe is still syncing with your bank — tap Refresh again in a few seconds.");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not refresh balances.");
    } finally {
      setRefreshingBalances(false);
    }
  };

  const removeBank = async (pmId, label) => {
    if (!window.confirm(`Remove ${label}? Stripe will detach this account and stop tracking it.`)) return;
    setRemovingPm(pmId);
    try {
      await api.delete(`/portal/banking/accounts/${encodeURIComponent(pmId)}`);
      toast.success(`${label} removed.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove bank.");
    } finally {
      setRemovingPm(null);
    }
  };

  const submitTransfer = async (payload) => {
    try {
      const { data: t } = await api.post("/portal/banking/transfers", payload);
      toast.success(`Transfer requested — Kalib will initiate the ACH shortly.`);
      setShowTransfer(false);
      setTransfers((prev) => [t, ...prev]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit transfer.");
    }
  };

  const cancelTransfer = async (id) => {
    if (!window.confirm("Cancel this transfer request?")) return;
    try {
      await api.delete(`/portal/banking/transfers/${id}`);
      toast.success("Transfer cancelled.");
      setTransfers((prev) => prev.map((t) => t.id === id ? { ...t, status: "cancelled" } : t));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel.");
    }
  };

  const connectBank = async () => {
    // Open the tab synchronously inside the click gesture so browsers don't
    // block it as a non-user action after the await (that popup block is why
    // clients saw "Popup blocked — link copied").
    const win = window.open("about:blank", "_blank");
    setBankingLoading(true);
    try {
      const { data: res } = await api.post("/portal/banking/start");
      if (res?.url) {
        if (win && !win.closed) {
          win.location.href = res.url;
          toast.success("Opening Stripe to link your bank…");
        } else {
          // Popup blocked — send them there in the same tab (Stripe returns them to the portal).
          window.location.href = res.url;
        }
        // Poll for the linked account for ~90s in case the webhook is slow
        // or unreachable (preview URLs). Sync pulls the details from Stripe.
        let tries = 0;
        const iv = setInterval(async () => {
          tries += 1;
          try {
            const { data: sync } = await api.post("/portal/banking/sync");
            if (sync?.linked) {
              clearInterval(iv);
              toast.success(`${sync.account?.brand || "Bank"} linked ✓`);
              // Kick a balance refresh so the card lands with a real number.
              try { await api.post("/portal/banking/balance/refresh"); } catch {}
              load();
            }
          } catch { /* keep polling */ }
          if (tries >= 30) clearInterval(iv);
        }, 3000);
      } else {
        if (win && !win.closed) win.close();
        toast.error("Stripe returned no URL. Try again in a minute.");
      }
    } catch (e) {
      if (win && !win.closed) win.close();
      toast.error(e?.response?.data?.detail || "Bank connect unavailable.");
    } finally {
      setBankingLoading(false);
    }
  };

  if (!data) {
    return (
      <div className="min-h-screen bg-[#eae7de] flex items-center justify-center gap-3 text-[#1e3d3a]/60 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading your portal…
      </div>
    );
  }

  if (access?.locked) {
    return (
      <div data-testid="portal-locked" className="min-h-screen bg-[#eae7de] text-[#1e3d3a] flex items-center justify-center px-6">
        <div className="max-w-md w-full rounded-2xl border border-[#b23a48]/25 bg-white p-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#b23a48]/12">
            <Lock className="h-6 w-6 text-[#b23a48]" />
          </div>
          <h1 className="font-head text-2xl">Subscription {access.status === "canceled" ? "inactive" : "past due"}</h1>
          <p className="mt-3 text-sm text-[#1e3d3a]/65">{access.reason || "Your subscription needs attention to restore access."}</p>
          <p className="mt-4 text-xs text-[#1e3d3a]/50">
            Reactivate your monthly plan to unlock your portal. Questions? Email{" "}
            <a href="mailto:hello@elevaresolutions.com" className="text-[#c9a961] underline">hello@elevaresolutions.com</a> or reach out to Kalib &amp; John.
          </p>
          <div className="mt-6 flex flex-col gap-2">
            <a data-testid="portal-locked-pay" href="mailto:hello@elevaresolutions.com?subject=Reactivate%20my%20Elevare%20subscription"
              className="rounded-full bg-[#c9a961] px-5 py-2.5 text-sm font-semibold text-[#1a2e2b] hover:bg-[#d8b972] transition-colors">
              Restore access
            </a>
            <button data-testid="portal-locked-signout" onClick={logout}
              className="text-xs uppercase tracking-[0.24em] text-[#1e3d3a]/50 hover:text-[#1e3d3a] transition-colors">
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { progress, expenses, next_booking, activity, identity, health } = data;
  const displayName = data.user?.name || user?.name || "there";
  const firstName = displayName.split(" ")[0];
  const selfService = Boolean(data.user?.self_service);

  return (
    <div className="min-h-screen bg-[#eae7de] text-[#1e3d3a]">
      <header className="max-w-6xl mx-auto w-full px-6 py-6 flex items-center justify-between">
        <Link to="/" data-testid="portal-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[#1e3d3a]/60 hover:text-[#1e3d3a] transition-colors duration-300">
          <ArrowLeft className="h-3.5 w-3.5" /> Elevare Solutions
        </Link>
        <div className="flex items-center gap-4">
          <button
            data-testid="portal-welcome-guide"
            onClick={downloadWelcomeGuide}
            disabled={welcomeDl}
            title="Download your illustrated welcome guide (PDF)"
            className="inline-flex items-center gap-1.5 rounded-full border border-[#c9a961]/50 bg-[#c9a961]/10 px-3.5 py-1.5 text-xs font-semibold text-[#1e3d3a] hover:bg-[#c9a961]/20 transition-colors duration-300 disabled:opacity-50"
          >
            {welcomeDl ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            <span className="hidden sm:inline">Welcome guide</span>
          </button>
          <button
            data-testid="portal-edit-email"
            onClick={() => { setNewEmail(""); setCurPw(""); setEmailModal(true); }}
            title="Change your email"
            className="hidden sm:inline-flex items-center gap-1.5 text-xs text-[#1e3d3a]/55 hover:text-[#a3823f] transition-colors duration-300 group"
          >
            {data.user?.email}
            <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
          </button>
          <button
            data-testid="portal-signout"
            onClick={signOut}
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] text-[#1e3d3a]/60 hover:text-[#b23a48] transition-colors duration-300"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto w-full px-6 pb-20">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <span className="text-[10px] uppercase tracking-[0.28em] text-[#c9a961]">Client Portal</span>
          <h1 className="mt-2 font-head text-4xl sm:text-5xl">
            Welcome back, {firstName}
          </h1>
          <p className="mt-2 text-sm text-[#1e3d3a]/65 max-w-xl">
            {selfService
              ? <>Your self-service portal — track expenses, jobs and finances in one place. Want us to dig into your numbers? <button data-testid="portal-hero-diagnostic" onClick={() => setFinTab("support")} className="underline decoration-[#c9a961] underline-offset-2 hover:text-[#1e3d3a]">Request a diagnostic</button>.</>
              : <>Everything Kalib and John have been building for you — recovery progress, savings identified, and what&rsquo;s next.</>}
          </p>
        </motion.div>

        <div className="mb-8 flex items-center gap-2 border-b border-[#1e3d3a]/10 overflow-x-auto" data-testid="portal-tabs">
          {[["progress", "My progress"], ["finances", "Business finances"], ["operations", "Operations"], ["crm", "CRM"], ["documents", "Documents"], ["workspace", "Workspace"], ["recommendations", "Recommendations"], ["handbook", "Handbook"], ["support", "Get help"]].map(([id, label]) => (
            <button key={id} data-testid={`portal-tab-${id}`} onClick={() => setFinTab(id)}
              className={`whitespace-nowrap px-4 py-2 text-xs uppercase tracking-[0.18em] -mb-px border-b-2 transition-colors duration-300 ${finTab === id ? "border-[#c9a961] text-[#1e3d3a]" : "border-transparent text-[#1e3d3a]/45 hover:text-[#1e3d3a]/70"}`}>
              {label}
            </button>
          ))}
        </div>

        {selfService && (finTab === "progress" || finTab === "recommendations") && (
          <DiagnosticNudge onRequest={() => setFinTab("support")} kind={finTab} />
        )}

        {finTab === "finances" && <PortalFinances money={money} />}

        {finTab === "operations" && <PortalOperations money={money} />}

        {finTab === "crm" && <><PortalTeamAccess /><PortalCRM /></>}

        {finTab === "documents" && <PortalDocuments />}

        {finTab === "workspace" && <Workspace apiBase="/workspace" />}

        {finTab === "recommendations" && <PortalRecommendations />}

        {finTab === "handbook" && <HandbookBoard handbookPath="/portal/handbook" pdfPath="/portal/handbook/pdf" downloadName="Elevare-Welcome-Guide.pdf" />}

        {finTab === "support" && <PortalSupport />}

        {finTab === "progress" && (<>
        {/* Identity + banking status card */}
        <IdentityBankingCard
          identity={identity}
          history={history}
          transfers={transfers}
          startIdentity={startIdentity}
          refreshIdentity={refreshIdentity}
          connectBank={connectBank}
          refreshBalances={refreshBalances}
          removeBank={removeBank}
          onTransfer={() => setShowTransfer(true)}
          onCancelTransfer={cancelTransfer}
          identityLoading={identityLoading}
          bankingLoading={bankingLoading}
          refreshingBalances={refreshingBalances}
          removingPm={removingPm}
        />
        {showTransfer && (
          <TransferModal
            banks={identity?.bank_accounts || []}
            onClose={() => setShowTransfer(false)}
            onSubmit={submitTransfer}
          />
        )}

        {/* Top stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="portal-stats">
          <HealthRingCard score={health?.score} scoredAt={health?.scored_at} />
          <StatCard
            icon={CheckCircle2}
            label="Milestones"
            value={progress ? `${progress.completed}/${progress.total_tasks}` : "—"}
            hint={progress ? `${Math.round((progress.completed / (progress.total_tasks || 1)) * 100)}% complete` : ""}
            testid="stat-milestones"
          />
          <StatCard
            icon={DollarSign}
            label="Monthly spend"
            value={expenses?.monthly_spend ? money(expenses.monthly_spend) : "—"}
            hint={expenses?.latest ? `From ${new Date(expenses.latest.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : "No analysis yet"}
            testid="stat-spend"
          />
          <StatCard
            icon={Sparkles}
            label="Savings identified"
            value={money(expenses?.total_savings_identified)}
            hint={`per month · ${money((expenses?.total_savings_identified || 0) * 12)} / yr`}
            testid="stat-savings"
            tone="forest"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
          {/* Recovery plan (spans full width when present) */}
          {plan && (
            <motion.div
              data-testid="portal-recovery-plan"
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="lg:col-span-4 rounded-2xl border border-[#c9a961]/40 bg-white overflow-hidden"
            >
              <div className="bg-[#1e3d3a] px-6 py-5 flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#c9a961]">
                    <ClipboardList className="h-3.5 w-3.5" /> Your recovery plan
                  </div>
                  <h2 className="mt-2 font-head text-2xl text-white">
                    {plan.business_name ? plan.business_name : "The plan we built for you"}
                  </h2>
                  {plan.approved_at && (
                    <p className="text-[11px] text-white/60 mt-1">
                      Approved {new Date(plan.approved_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  {plan.expected_monthly_savings > 0 && (
                    <>
                      <div className="text-[10px] uppercase tracking-[0.24em] text-[#c9a961]/80">Projected savings</div>
                      <div className="font-head text-2xl text-[#c9a961]">{money(plan.expected_monthly_savings)}<span className="text-sm text-white/60"> / mo</span></div>
                      <div className="text-[10px] text-white/50">{money(plan.expected_monthly_savings * 12)} per year</div>
                    </>
                  )}
                  {plan.timeline_days > 0 && (
                    <div className="mt-1 text-[10px] uppercase tracking-[0.22em] text-white/50">
                      ~{plan.timeline_days} day timeline
                    </div>
                  )}
                </div>
              </div>
              <div className="p-6 space-y-5">
                {plan.problem_summary && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-[#c9a961] mb-2">The problem</div>
                    <p className="text-sm text-[#1e3d3a] leading-relaxed">{plan.problem_summary}</p>
                  </div>
                )}
                {plan.root_causes?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-[#c9a961] mb-2">Root causes</div>
                    <ul className="space-y-1">
                      {plan.root_causes.map((c, i) => (
                        <li key={i} className="text-sm text-[#1e3d3a] flex gap-2">
                          <span className="text-[#c9a961]">·</span> {c}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {plan.phases?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-[#c9a961] mb-3">Phased plan</div>
                    <ol className="space-y-4">
                      {plan.phases.map((p, i) => (
                        <li key={i} className="rounded-xl border border-[#1e3d3a]/10 bg-[#eae7de]/40 p-4">
                          <div className="flex items-baseline justify-between gap-2 flex-wrap">
                            <div className="flex items-baseline gap-2">
                              <span className="font-head text-lg text-[#c9a961]">Phase {p.phase || i + 1}</span>
                              <span className="text-sm text-[#1e3d3a]">{p.focus}</span>
                            </div>
                            {p.duration_weeks && (
                              <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55">
                                {p.duration_weeks} weeks
                              </span>
                            )}
                          </div>
                          {(p.actions || []).length > 0 && (
                            <ul className="mt-2 space-y-1.5">
                              {p.actions.map((a, j) => (
                                <RecoveryStep
                                  key={j}
                                  stepText={a}
                                  testid={`portal-recovery-step-${i}-${j}`}
                                  loadDetail={() => api.post("/portal/recovery-plan/step-detail", {
                                    phase_index: i, step_index: j, step_text: a,
                                  }).then((r) => r.data)}
                                  saveItem={(itemIndex, done) => api.put("/portal/recovery-plan/step-detail/checklist", {
                                    phase_index: i, step_index: j, item_index: itemIndex, done,
                                  })}
                                />
                              ))}
                            </ul>
                          )}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Progress + Milestones (left, larger) */}
          <div className="lg:col-span-3 space-y-5 min-w-0">
            <div className="rounded-2xl border border-[#1e3d3a]/12 bg-[#1e3d3a] text-[#c9a961] p-6">
              <div className="text-[10px] uppercase tracking-[0.24em] opacity-70">Need something?</div>
              <p className="mt-2 text-sm text-white/90">Message Kalib or John any time — they&rsquo;ll get back to you inside a business day.</p>
              <a href="mailto:kalib@elevaresolutions.me,john@elevaresolutions.me" className="mt-4 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-[#c9a961] hover:text-white transition-colors duration-300">
                Email the team <ExternalLink className="h-3 w-3" />
              </a>
            </div>
            {progress && (
              <div data-testid="portal-progress" className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] uppercase tracking-[0.24em] text-[#c9a961] flex items-center gap-1.5">
                    <TrendingUp className="h-3 w-3" /> BBB progress
                  </span>
                  <span className="text-xs text-[#1e3d3a]/50 tabular-nums">{progress.progress_pct}%</span>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="rounded-xl border border-[#1e3d3a]/12 bg-[#eae7de]/60 px-4 py-3 text-center">
                    <div className="text-[9px] uppercase tracking-[0.24em] text-[#1e3d3a]/55">Current</div>
                    <div className="mt-1 font-head text-4xl font-light">{progress.current_bbb || "—"}</div>
                  </div>
                  <div className="rounded-xl border border-[#c9a961]/40 bg-[#c9a961]/8 px-4 py-3 text-center">
                    <div className="text-[9px] uppercase tracking-[0.24em] text-[#a3823f]">Target</div>
                    <div className="mt-1 font-head text-4xl font-light">{progress.target_bbb || "—"}</div>
                  </div>
                </div>
                <div className="relative h-2.5 rounded-full bg-[#eae7de] overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progress.progress_pct}%` }}
                    transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#c9a961] to-[#1e3d3a] rounded-full"
                  />
                  {progress.progress_pct >= 100 && (
                    <Trophy className="absolute right-1 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#c9a961]" />
                  )}
                </div>
                {progress.history?.length > 0 && (
                  <div className="mt-4 flex flex-wrap items-center gap-1.5 text-sm">
                    {progress.history.slice(-8).map((h, i, arr) => (
                      <span key={i} className="flex items-center gap-1.5">
                        <span className="font-head text-base text-[#1e3d3a]">{h.rating}</span>
                        <span className="text-[10px] text-[#1e3d3a]/45">{new Date(h.at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                        {i < arr.length - 1 && <span className="text-[#c9a961]">→</span>}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {progress?.tasks?.length > 0 && (
              <div data-testid="portal-milestones" className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] uppercase tracking-[0.24em] text-[#c9a961]">Milestones</span>
                  <span className="text-xs text-[#1e3d3a]/50 tabular-nums">{progress.completed}/{progress.total_tasks}</span>
                </div>
                <p className="text-[11px] text-[#1e3d3a]/50 mb-4">Attach receipts, screenshots, or proof so Kalib &amp; John can verify progress.</p>
                <ul className="space-y-2">
                  {progress.tasks.map((t, i) => {
                    const atts = t.attachments || [];
                    const uploading = uploadingIdx === i;
                    return (
                      <li key={i} className="rounded-lg hover:bg-[#eae7de]/40 transition-colors duration-300 px-2 py-2">
                        <div className="flex items-center gap-2 group">
                          {t.done
                            ? <CheckCircle2 className="h-4 w-4 text-[#c9a961] shrink-0" strokeWidth={1.8} />
                            : <Circle className="h-4 w-4 text-[#1e3d3a]/30 shrink-0" strokeWidth={1.6} />}
                          <span className={`flex-1 text-sm ${t.done ? "text-[#1e3d3a]/45 line-through" : "text-[#1e3d3a]"}`}>{t.label}</span>
                          <label
                            data-testid={`portal-attach-${i}`}
                            className="inline-flex items-center gap-1 text-[11px] text-[#1e3d3a]/45 hover:text-[#a3823f] cursor-pointer transition-colors duration-300"
                          >
                            {uploading
                              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              : <Paperclip className="h-3.5 w-3.5" strokeWidth={1.6} />}
                            <span className="hidden sm:inline">Attach</span>
                            <input
                              type="file"
                              className="hidden"
                              disabled={uploading}
                              onChange={(e) => { if (e.target.files?.[0]) upload(i, e.target.files[0]); e.target.value = ""; }}
                            />
                          </label>
                        </div>
                        {atts.length > 0 && (
                          <ul className="mt-1 ml-6 space-y-1">
                            {atts.map((att) => {
                              const st = att.review_status || "pending";
                              const stTone = st === "approved"
                                ? "bg-[#6b7c3a]/15 text-[#4d5a24]"
                                : st === "rejected"
                                ? "bg-[#b23a48]/12 text-[#b23a48]"
                                : "bg-[#c9a961]/18 text-[#a3823f]";
                              const stLabel = st === "approved"
                                ? "Approved"
                                : st === "rejected"
                                ? "Needs re-upload"
                                : "Under review";
                              return (
                                <li
                                  key={att.id}
                                  data-testid={`portal-attachment-${att.id}`}
                                  className="flex flex-wrap items-center gap-2 text-[11px] text-[#1e3d3a]/70"
                                >
                                  <FileText className="h-3 w-3 shrink-0" />
                                  <span className="truncate flex-1 min-w-[8rem]">{att.filename}</span>
                                  <span
                                    data-testid={`portal-attach-status-${att.id}`}
                                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] ${stTone}`}
                                    title={att.reviewed_at ? `Reviewed ${new Date(att.reviewed_at).toLocaleString()}` : "Waiting on Kalib & John to review"}
                                  >
                                    {st === "approved" && <Check className="h-2.5 w-2.5" />}
                                    {stLabel}
                                  </span>
                                  <button
                                    type="button"
                                    data-testid={`portal-attach-download-${att.id}`}
                                    onClick={() => downloadAttachment(i, att)}
                                    className="text-[#1e3d3a]/45 hover:text-[#a3823f] transition-colors duration-300"
                                    title="Download your copy"
                                    aria-label={`Download ${att.filename}`}
                                  >
                                    <Download className="h-3.5 w-3.5" />
                                  </button>
                                  {canPreview(att) && (
                                    <button
                                      type="button"
                                      data-testid={`portal-attach-preview-${att.id}`}
                                      onClick={() => previewAttachment(i, att)}
                                      className="text-[#1e3d3a]/45 hover:text-[#a3823f] transition-colors duration-300"
                                      title="Preview"
                                      aria-label={`Preview ${att.filename}`}
                                    >
                                      <Eye className="h-3.5 w-3.5" />
                                    </button>
                                  )}
                                  {st !== "approved" && (
                                    <button
                                      type="button"
                                      data-testid={`portal-attach-remove-${att.id}`}
                                      onClick={() => removeAttachment(i, att)}
                                      className="text-[#1e3d3a]/45 hover:text-[#b23a48] transition-colors duration-300"
                                      title="Remove this document so you can upload a new one"
                                      aria-label={`Remove ${att.filename}`}
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* My Documents — engagement authorization + cross-milestone file view */}
            {(() => {
              const hasAtts = (progress?.tasks || []).some((t) => (t.attachments || []).length > 0);
              return (
              <div data-testid="portal-my-documents" className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] uppercase tracking-[0.24em] text-[#c9a961] flex items-center gap-1.5">
                    <FolderOpen className="h-3 w-3" /> My documents
                  </span>
                  {hasAtts && (() => {
                    let pending = 0, approved = 0, rejected = 0;
                    progress.tasks.forEach((t) => (t.attachments || []).forEach((a) => {
                      const st = a.review_status || "pending";
                      if (st === "approved") approved++;
                      else if (st === "rejected") rejected++;
                      else pending++;
                    }));
                    return (
                      <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 tabular-nums">
                        {approved > 0 && <span className="text-[#4d5a24]">{approved} approved</span>}
                        {approved > 0 && (pending > 0 || rejected > 0) && " · "}
                        {pending > 0 && <span className="text-[#a3823f]">{pending} in review</span>}
                        {pending > 0 && rejected > 0 && " · "}
                        {rejected > 0 && <span className="text-[#b23a48]">{rejected} needs re-upload</span>}
                      </span>
                    );
                  })()}
                </div>

                {/* Engagement authorization — built into My documents */}
                <div className="rounded-xl border border-[#c9a961]/25 bg-[#c9a961]/5 p-4 mb-5">
                  <EngagementAuthorizationCard embedded />
                </div>

                {hasAtts ? (
                <p className="text-[11px] text-[#1e3d3a]/50 mb-4">
                  Every file you&rsquo;ve sent us, grouped by milestone. Click any row to download your copy.
                </p>
                ) : (
                <p className="text-[11px] text-[#1e3d3a]/50 mb-1">
                  You haven&rsquo;t uploaded any documents yet. Files you send us will appear here.
                </p>
                )}
                {hasAtts && (
                <ul className="divide-y divide-[#1e3d3a]/8">
                  {progress.tasks.map((t, i) => {
                    const atts = t.attachments || [];
                    if (atts.length === 0) return null;
                    const sortedAtts = [...atts].sort(
                      (a, b) => (b.uploaded_at || "").localeCompare(a.uploaded_at || ""),
                    );
                    return (
                      <li key={i} className="py-2.5">
                        <div className="flex items-center gap-2 mb-1.5">
                          {t.done
                            ? <CheckCircle2 className="h-3.5 w-3.5 text-[#c9a961] shrink-0" strokeWidth={1.8} />
                            : <Circle className="h-3.5 w-3.5 text-[#1e3d3a]/30 shrink-0" strokeWidth={1.6} />}
                          <span className={`text-xs font-medium ${t.done ? "text-[#1e3d3a]/60" : "text-[#1e3d3a]"}`}>{t.label}</span>
                          <span className="ml-auto text-[9px] uppercase tracking-[0.22em] text-[#1e3d3a]/45 tabular-nums">{atts.length} file{atts.length !== 1 ? "s" : ""}</span>
                        </div>
                        <ul className="ml-5 space-y-1">
                          {sortedAtts.map((att) => {
                            const st = att.review_status || "pending";
                            const stTone = st === "approved"
                              ? "bg-[#6b7c3a]/15 text-[#4d5a24]"
                              : st === "rejected"
                              ? "bg-[#b23a48]/12 text-[#b23a48]"
                              : "bg-[#c9a961]/18 text-[#a3823f]";
                            const stLabel = st === "approved"
                              ? "Approved"
                              : st === "rejected"
                              ? "Needs re-upload"
                              : "Under review";
                            return (
                              <li
                                key={att.id}
                                data-testid={`portal-mydocs-${att.id}`}
                                onClick={() => downloadAttachment(i, att)}
                                className="flex flex-wrap items-center gap-2 text-[11px] text-[#1e3d3a]/70 rounded-md px-2 py-1.5 hover:bg-[#c9a961]/8 cursor-pointer transition-colors duration-300"
                              >
                                <FileText className="h-3 w-3 shrink-0 text-[#a3823f]" />
                                <span className="truncate flex-1 min-w-[6rem] text-[#1e3d3a]">{att.filename}</span>
                                {att.size && (
                                  <span className="text-[9px] text-[#1e3d3a]/45 tabular-nums">
                                    {att.size > 1024*1024 ? `${(att.size/1024/1024).toFixed(1)} MB` : `${Math.round(att.size/1024)} KB`}
                                  </span>
                                )}
                                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] ${stTone}`}
                                      title={att.reviewed_at
                                        ? `Reviewed ${new Date(att.reviewed_at).toLocaleString()}${att.reviewed_by ? ` by ${att.reviewed_by}` : ""}`
                                        : "Waiting on Kalib & John"}>
                                  {st === "approved" && <Check className="h-2.5 w-2.5" />}
                                  {stLabel}
                                </span>
                                <Download className="h-3.5 w-3.5 text-[#1e3d3a]/40" />
                                {canPreview(att) && (
                                  <button
                                    type="button"
                                    data-testid={`portal-mydocs-preview-${att.id}`}
                                    onClick={(ev) => { ev.stopPropagation(); previewAttachment(i, att); }}
                                    className="text-[#1e3d3a]/40 hover:text-[#a3823f] transition-colors duration-300"
                                    title="Preview"
                                    aria-label={`Preview ${att.filename}`}
                                  >
                                    <Eye className="h-3.5 w-3.5" />
                                  </button>
                                )}
                                {st !== "approved" && (
                                  <button
                                    type="button"
                                    data-testid={`portal-mydocs-remove-${att.id}`}
                                    onClick={(ev) => { ev.stopPropagation(); removeAttachment(i, att); }}
                                    className="text-[#1e3d3a]/40 hover:text-[#b23a48] transition-colors duration-300"
                                    title="Remove this document"
                                    aria-label={`Remove ${att.filename}`}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </li>
                    );
                  })}
                </ul>
                )}
              </div>
              );
            })()}

            {/* Expense history */}
            {expenses?.history?.length > 0 && (
              <div data-testid="portal-expense-history" className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-6">
                <span className="text-[10px] uppercase tracking-[0.24em] text-[#c9a961]">Recovery worksheets</span>
                <ul className="mt-3 divide-y divide-[#1e3d3a]/10">
                  {expenses.history.map((h) => (
                    <li key={h.id} className="flex flex-wrap items-center justify-between gap-2 py-3">
                      <div className="min-w-0">
                        <div className="text-sm text-[#1e3d3a] truncate">{h.business_name || "Expense worksheet"}</div>
                        <div className="text-[10px] text-[#1e3d3a]/45 uppercase tracking-[0.18em]">
                          {new Date(h.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })} · Spend {money(h.total_monthly)}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-head text-lg text-[#1e3d3a]">{money(h.total_potential_monthly_savings)}</div>
                        <div className="text-[10px] text-[#1e3d3a]/45 uppercase tracking-[0.18em]">savings / mo</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Right column */}
          <div className="grid auto-rows-fr gap-5 content-stretch min-w-0">
            {next_booking && (
              <div data-testid="portal-next-booking" className="rounded-2xl border border-[#c9a961]/40 bg-[#c9a961]/10 p-6 min-w-0">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">
                  <Calendar className="h-3.5 w-3.5" /> Next call
                </div>
                <div className="mt-2 font-head text-xl text-[#1e3d3a]">
                  {new Date(next_booking.slot_iso).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
                </div>
                <div className="text-sm text-[#1e3d3a]/70">
                  {new Date(next_booking.slot_iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                </div>
                {next_booking.goal && (
                  <p className="mt-3 text-[12px] text-[#1e3d3a]/60 italic">&ldquo;{next_booking.goal}&rdquo;</p>
                )}
                {next_booking.manage_url && (
                  <a
                    href={next_booking.manage_url}
                    className="mt-4 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-[#a3823f] hover:text-[#1e3d3a] transition-colors duration-300"
                    data-testid="portal-manage-booking"
                  >
                    Reschedule or cancel <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            )}

            {activity?.length > 0 && (
              <div data-testid="portal-activity" className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-6 min-w-0">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-[#c9a961]">
                  <Activity className="h-3.5 w-3.5" /> Recent activity
                </div>
                <ul className="mt-3 space-y-2">
                  {activity.map((a, i) => (
                    <li key={i} className="text-xs text-[#1e3d3a] flex gap-2">
                      <span className="text-[#c9a961] shrink-0">·</span>
                      <div className="min-w-0">
                        <div className="truncate">{a.message}</div>
                        {a.at && (
                          <div className="text-[10px] text-[#1e3d3a]/45">
                            {new Date(a.at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <AdvisorMeetingCard />
            <ReferBusinessCard />
          </div>
        </div>

        {/* Recurring payment authorization — pinned to the very bottom */}
        <div className="mt-6">
          <AchAuthorizationCard bankAccounts={data?.identity?.bank_accounts || []} />
        </div>
        </>)}
      </main>

      {preview && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-[#1e3d3a]/60 backdrop-blur-sm"
          data-testid="portal-preview-modal"
          onClick={(e) => { if (e.target === e.currentTarget) closePreview(); }}
        >
          <div className="relative w-full max-w-4xl h-[85vh] rounded-2xl bg-white shadow-2xl border border-[#c9a961]/25 flex flex-col overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-[#1e3d3a]/10">
              <div className="min-w-0 flex items-center gap-2">
                <FileText className="h-4 w-4 text-[#a3823f] shrink-0" />
                <span className="truncate text-sm font-medium text-[#1e3d3a]">{preview.filename}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a
                  href={preview.url}
                  download={preview.filename}
                  data-testid="portal-preview-download"
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-4 py-1.5 text-[11px] uppercase tracking-[0.18em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300"
                >
                  <Download className="h-3.5 w-3.5" /> Download
                </a>
                <button
                  type="button"
                  data-testid="portal-preview-close"
                  onClick={closePreview}
                  className="text-[#1e3d3a]/45 hover:text-[#1e3d3a] transition-colors duration-300 p-1"
                  aria-label="Close preview"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 bg-[#eae7de]/40 overflow-auto flex items-center justify-center">
              {preview.mime.startsWith("image/") ? (
                <img src={preview.url} alt={preview.filename} className="max-w-full max-h-full object-contain" />
              ) : (
                <iframe src={preview.url} title={preview.filename} className="w-full h-full" />
              )}
            </div>
          </div>
        </div>
      )}
      {emailModal && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-[#1e3d3a]/60 backdrop-blur-sm"
          data-testid="portal-email-modal"
          onClick={(e) => { if (e.target === e.currentTarget && !emailBusy) setEmailModal(false); }}
        >
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl border border-[#c9a961]/25">
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#a3823f]">Account</p>
            <h3 className="mt-1 font-head text-2xl text-[#1e3d3a]">Change your email</h3>
            <p className="mt-2 text-sm text-[#1e3d3a]/70">
              This becomes your new sign-in email and updates it everywhere — your reports,
              bookings, payments, progress and documents all stay linked.
            </p>
            <div className="mt-4 space-y-2.5">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.22em] text-[#a3823f] mb-1">New email</label>
                <input
                  data-testid="portal-email-new"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full rounded-lg border border-[#1e3d3a]/15 bg-[#eae7de]/60 px-3 py-2 text-sm focus:border-[#c9a961] focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.22em] text-[#a3823f] mb-1">Current password</label>
                <input
                  data-testid="portal-email-password"
                  type="password"
                  value={curPw}
                  onChange={(e) => setCurPw(e.target.value)}
                  placeholder="Leave blank if you sign in with Google"
                  className="w-full rounded-lg border border-[#1e3d3a]/15 bg-[#eae7de]/60 px-3 py-2 text-sm focus:border-[#c9a961] focus:outline-none"
                />
              </div>
            </div>
            <div className="mt-5 flex items-center gap-3">
              <button
                type="button"
                data-testid="portal-email-save"
                onClick={changeEmail}
                disabled={emailBusy}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#1e3d3a] text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300"
              >
                {emailBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />} Update email
              </button>
              <button
                type="button"
                onClick={() => setEmailModal(false)}
                disabled={emailBusy}
                className="text-[11px] uppercase tracking-[0.22em] text-black/45 hover:text-[#1e3d3a] px-3 disabled:opacity-60"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function IdentityBankingCard({ identity, history, transfers, startIdentity, refreshIdentity, connectBank, refreshBalances, removeBank, onTransfer, onCancelTransfer, identityLoading, bankingLoading, refreshingBalances, removingPm }) {
  // Never rendered if the backend hasn't materialised a vault yet AND
  // identity was never started — show only after the user has a record.
  const status = identity?.status || "not_started";
  const verified = Boolean(identity?.verified);
  const banks = identity?.bank_accounts || [];
  // Only "processing" (docs submitted, Stripe reviewing) is a true in-progress
  // state. "requires_input"/"canceled"/"created" mean the client never finished,
  // so we send them back to the start step instead of a dead-end refresh.
  const inProgress = status === "processing";
  const needsRestart = ["requires_input", "canceled", "created"].includes(status);
  const hidden = !identity && !verified;
  if (hidden) {
    // First-time nudge — always render so the client can kick off identity.
  }
  return (
    <motion.section
      data-testid="portal-identity-card"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className={`mb-6 rounded-2xl border overflow-hidden ${verified ? "border-[#6b7c3a]/40 bg-white" : "border-[#c9a961]/50 bg-[#c9a961]/8"}`}
    >
      <div className="p-6 flex flex-wrap items-start gap-6">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">
            <ShieldCheck className="h-3.5 w-3.5" /> Identity &amp; banking
          </div>
          {verified ? (
            <>
              <h3 className="mt-2 font-head text-xl text-[#1e3d3a]">
                Identity verified · powered by Stripe
              </h3>
              <p className="mt-1 text-[13px] text-[#1e3d3a]/65">
                You're cleared for secure banking. Connect a business bank account through Stripe Financial Connections when you're ready — we only ever see the last 4 of the account.
              </p>
            </>
          ) : inProgress ? (
            <>
              <h3 className="mt-2 font-head text-xl text-[#1e3d3a]">
                Verification in progress
              </h3>
              <p className="mt-1 text-[13px] text-[#1e3d3a]/65">
                Stripe is reviewing your documents. This usually takes a few minutes. Refresh below once you get Stripe's confirmation email.
              </p>
            </>
          ) : (
            <>
              <h3 className="mt-2 font-head text-xl text-[#1e3d3a]">
                {needsRestart ? "Finish verifying your identity" : "One-time identity check (~2 minutes)"}
              </h3>
              <p className="mt-1 text-[13px] text-[#1e3d3a]/65">
                {needsRestart
                  ? "Looks like your last attempt wasn't completed. No problem — tap below to start the identity check again. Your ID goes straight to Stripe; we never see or store it."
                  : "Before we set up secure banking or move any funds on your behalf, we ask every client to confirm their identity through Stripe. Your ID goes straight to Stripe — we never see or store it."}
              </p>
            </>
          )}
          {banks.length > 0 && (
            <div className="mt-6" data-testid="portal-bank-list">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] uppercase tracking-[0.22em] text-[#a3823f]">
                  Live cashflow · {banks.length} account{banks.length === 1 ? "" : "s"}
                </span>
                <div className="flex items-center gap-3">
                  {banks.length >= 2 && (
                    <button
                      type="button"
                      data-testid="portal-transfer-open"
                      onClick={onTransfer}
                      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 hover:text-[#a3823f] transition-colors duration-300"
                    >
                      <ArrowRightLeft className="h-3 w-3" /> Transfer
                    </button>
                  )}
                  <button
                    type="button"
                    data-testid="portal-refresh-balances"
                    onClick={refreshBalances}
                    disabled={refreshingBalances}
                    className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 hover:text-[#a3823f] disabled:opacity-60 transition-colors duration-300"
                  >
                    {refreshingBalances ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                    Refresh
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {banks.map((b, i) => (
                  <BankBalanceCard
                    key={b.id || b.payment_method_id || b.fc_account_id || i}
                    bank={b}
                    history={history?.[b.fc_account_id] || []}
                    onRemove={removeBank && !b.is_manual && (b.payment_method_id || b.fc_account_id)
                      ? () => removeBank(b.payment_method_id || b.fc_account_id, `${b.brand} ••••${b.last4 || ""}`)
                      : null}
                    removing={removingPm === (b.payment_method_id || b.fc_account_id)}
                  />
                ))}
              </div>

              {/* Recent transfers list */}
              {transfers?.length > 0 && (
                <div className="mt-5 rounded-xl border border-[#1e3d3a]/10 bg-white/60 p-3">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#a3823f] mb-2 flex items-center gap-1.5">
                    <Clock className="h-3 w-3" /> Recent transfers
                  </div>
                  <ul className="space-y-1.5" data-testid="portal-transfers-list">
                    {transfers.slice(0, 5).map((t) => {
                      const amt = `$${(t.amount_cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
                      const statusColor = {
                        requested: "text-[#a3823f]",
                        in_progress: "text-[#c9a961]",
                        completed: "text-[#4d5a24]",
                        cancelled: "text-black/40",
                        failed: "text-[#b23a48]",
                      }[t.status] || "text-black/50";
                      return (
                        <li key={t.id} className="flex items-center justify-between gap-3 text-[11px] text-[#1e3d3a]/80">
                          <div className="min-w-0 flex-1 flex items-center gap-2">
                            <span className="tabular-nums font-medium">{amt}</span>
                            <span className="text-[#1e3d3a]/45">·</span>
                            <span className="truncate">{t.from?.brand} ••••{t.from?.last4}</span>
                            <ArrowRightLeft className="h-2.5 w-2.5 text-[#a3823f] shrink-0" />
                            <span className="truncate">{t.to?.brand} ••••{t.to?.last4}</span>
                          </div>
                          <span className={`text-[9px] uppercase tracking-[0.2em] shrink-0 ${statusColor}`}>{t.status}</span>
                          {(t.status === "requested" || t.status === "in_progress") && (
                            <button
                              type="button"
                              onClick={() => onCancelTransfer(t.id)}
                              className="text-[#1e3d3a]/35 hover:text-[#b23a48] transition-colors duration-300"
                              title="Cancel"
                              data-testid={`portal-transfer-cancel-${t.id}`}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {verified ? (
            <button
              type="button"
              data-testid="portal-connect-bank"
              onClick={connectBank}
              disabled={bankingLoading}
              className="inline-flex items-center gap-2 rounded-full bg-[#1e3d3a] text-white px-4 py-2 text-[11px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300"
            >
              {bankingLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Landmark className="h-3.5 w-3.5" />}
              {banks.length > 0 ? "Link another bank" : "Connect a bank"}
            </button>
          ) : inProgress ? (
            <button
              type="button"
              data-testid="portal-refresh-identity"
              onClick={refreshIdentity}
              className="inline-flex items-center gap-2 rounded-full border border-[#c9a961]/60 bg-white text-[#1e3d3a] px-4 py-2 text-[11px] uppercase tracking-[0.22em] hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300"
            >
              <Loader2 className="h-3.5 w-3.5" /> Refresh status
            </button>
          ) : (
            <button
              type="button"
              data-testid="portal-start-identity"
              onClick={startIdentity}
              disabled={identityLoading}
              className="inline-flex items-center gap-2 rounded-full bg-[#1e3d3a] text-white px-4 py-2 text-[11px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300"
            >
              {identityLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Lock className="h-3.5 w-3.5" />}
              {needsRestart ? "Try again" : "Verify my identity"}
            </button>
          )}
        </div>
      </div>
    </motion.section>
  );
}


function BankBalanceCard({ bank, history, onRemove, removing }) {
  const bal = bank?.balance;
  const availableCents = bal?.available_cents;
  const currentCents = bal?.current_cents;
  const primary = availableCents ?? currentCents;
  const usd = (cents) =>
    typeof cents === "number"
      ? `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
      : "—";
  const asOf = bal?.as_of ? new Date(bal.as_of * 1000) : (bal?.fetched_at ? new Date(bal.fetched_at) : null);
  const asOfLabel = asOf ? asOf.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : null;
  const brand = bank?.brand || "Bank";
  const last4 = bank?.last4;

  // Compute 30-day trend delta if we have history.
  const pts = Array.isArray(history) ? history : [];
  const firstVal = pts.length ? (pts[0].available_cents ?? pts[0].current_cents) : null;
  const lastVal = pts.length ? (pts[pts.length - 1].available_cents ?? pts[pts.length - 1].current_cents) : null;
  const deltaCents = (typeof firstVal === "number" && typeof lastVal === "number") ? lastVal - firstVal : null;
  const deltaPct = (deltaCents != null && firstVal) ? (deltaCents / firstVal) * 100 : null;
  const trendUp = deltaCents != null && deltaCents >= 0;

  return (
    <div
      data-testid={`bank-balance-${last4 || "unknown"}`}
      className="relative rounded-xl border border-[#1e3d3a]/10 bg-gradient-to-br from-[#1e3d3a] to-[#2a4f4a] p-4 text-white overflow-hidden"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] text-[#c9a961]">
            <Landmark className="h-3 w-3" />
            <span className="truncate">{brand}</span>
          </div>
          {last4 && (
            <div className="mt-0.5 text-[11px] text-white/50 tabular-nums">•••• {last4}</div>
          )}
        </div>
      </div>
      <div className="mt-4">
        <p className="text-[10px] uppercase tracking-[0.22em] text-white/45">
          {bank?.is_manual ? "Reference only" : (availableCents != null ? "Available" : "Current")}
        </p>
        <p
          data-testid={`bank-balance-amount-${last4 || "unknown"}`}
          className={`mt-1 font-head font-light tabular-nums ${primary != null ? "text-3xl" : "text-lg text-white/40"}`}
        >
          {primary != null ? usd(primary) : (bank?.is_manual ? "—" : "Syncing…")}
        </p>
      </div>
      {primary != null && currentCents != null && availableCents != null && currentCents !== availableCents && (
        <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-white/40">
          Ledger {usd(currentCents)}
        </p>
      )}

      {/* Sparkline */}
      {pts.length >= 2 && (
        <div className="mt-3" data-testid={`bank-spark-${last4 || "unknown"}`}>
          <Sparkline points={pts} up={trendUp} />
          <div className="mt-1 flex items-center justify-between text-[9px] uppercase tracking-[0.22em] text-white/40">
            <span>30-day</span>
            <span className={trendUp ? "text-[#c9a961]" : "text-[#e07a5f]"}>
              {deltaCents != null ? `${trendUp ? "▲" : "▼"} ${usd(Math.abs(deltaCents))}${deltaPct != null ? ` (${trendUp ? "+" : ""}${deltaPct.toFixed(1)}%)` : ""}` : ""}
            </span>
          </div>
        </div>
      )}

      {asOfLabel && !bank?.is_manual && (
        <p className="mt-3 text-[9px] uppercase tracking-[0.22em] text-white/35">
          As of {asOfLabel}
        </p>
      )}
      {bank?.is_manual && (
        <p className="mt-3 text-[10px] text-white/40 leading-relaxed">
          Manually recorded · balance not synced. Link this bank via Stripe to see live cashflow.
        </p>
      )}
      {!bal && !bank?.is_manual && (
        <p className="mt-3 text-[10px] text-white/40 leading-relaxed">
          Balance will appear once your bank finishes syncing (~5–10s). Click <span className="text-[#c9a961]">Refresh</span> above if it doesn&rsquo;t land within a minute.
        </p>
      )}
      {bal?.pending && (
        <p className="mt-3 text-[10px] text-[#c9a961]/80 leading-relaxed inline-flex items-center gap-1.5">
          <Loader2 className="h-3 w-3 animate-spin" /> Waiting on your bank — {bal.status || "pending"}
        </p>
      )}
      {onRemove && (
        <button
          type="button"
          data-testid={`bank-remove-${last4 || "unknown"}`}
          onClick={onRemove}
          disabled={removing}
          className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-white/25 px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.14em] text-white/75 hover:border-[#e07a5f]/70 hover:text-[#e07a5f] hover:bg-white/5 transition-colors duration-300 disabled:opacity-40"
          title="Disconnect this bank account"
        >
          {removing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" strokeWidth={1.8} />}
          {removing ? "Disconnecting…" : "Disconnect bank"}
        </button>
      )}
    </div>
  );
}

function Sparkline({ points, up }) {
  // Points is an array of { day, available_cents, current_cents }.
  const values = points.map((p) => Number(p.available_cents ?? p.current_cents ?? 0));
  if (values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = Math.max(1, max - min);
  const W = 200, H = 34;
  const step = W / (values.length - 1);
  const pts = values.map((v, i) => {
    const x = (i * step).toFixed(1);
    const y = (H - ((v - min) / range) * (H - 4) - 2).toFixed(1);
    return `${x},${y}`;
  }).join(" ");
  const stroke = up ? "#c9a961" : "#e07a5f";
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-8" preserveAspectRatio="none" aria-hidden>
      <polyline points={pts} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function TransferModal({ banks, onClose, onSubmit }) {
  const [fromId, setFromId] = useState(banks[0]?.id || "");
  const [toId, setToId] = useState(banks[1]?.id || "");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [when, setWhen] = useState("asap");
  const [submitting, setSubmitting] = useState(false);

  const dollars = Number((amount || "0").replace(/[^0-9.]/g, "")) || 0;
  const from = banks.find((b) => b.id === fromId);
  const to = banks.find((b) => b.id === toId);
  const fromAvail = (from?.balance?.available_cents ?? from?.balance?.current_cents ?? 0) / 100;
  const canSubmit = fromId && toId && fromId !== toId && dollars > 0;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onSubmit({
        from_payment_method_id: fromId,
        to_payment_method_id: toId,
        amount_cents: Math.round(dollars * 100),
        note: note || null,
        when,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const selectCls = "w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/20 transition-colors duration-300";

  return (
    <div
      data-testid="transfer-modal"
      className="fixed inset-0 z-50 bg-black/45 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.22 }}
        className="w-full max-w-lg rounded-2xl bg-[#ded9cb] border border-[#c9a961]/40 p-6 shadow-2xl"
      >
        <div className="flex items-center justify-between mb-1">
          <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">Move funds</p>
          <button type="button" onClick={onClose} data-testid="transfer-modal-close" className="text-black/40 hover:text-[#1e3d3a] transition-colors duration-300">
            <X className="h-4 w-4" />
          </button>
        </div>
        <h3 className="mt-1 font-head text-2xl text-[#1e3d3a]">Request a transfer</h3>
        <p className="mt-1 text-xs text-black/55 leading-relaxed">
          We&rsquo;ll route this to Kalib &amp; John to initiate the ACH securely. You&rsquo;ll get a
          confirmation email once the money is on its way.
        </p>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.22em] text-black/45">From</label>
            <select data-testid="transfer-from" className={"mt-1 " + selectCls} value={fromId} onChange={(e) => setFromId(e.target.value)}>
              {banks.map((b) => (
                <option key={b.id} value={b.id}>{b.brand} ••••{b.last4}</option>
              ))}
            </select>
            {from?.balance && (
              <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-black/40 tabular-nums">
                Available ${fromAvail.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </p>
            )}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.22em] text-black/45">To</label>
            <select data-testid="transfer-to" className={"mt-1 " + selectCls} value={toId} onChange={(e) => setToId(e.target.value)}>
              {banks.filter((b) => b.id !== fromId).map((b) => (
                <option key={b.id} value={b.id}>{b.brand} ••••{b.last4}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.22em] text-black/45">Amount ($)</label>
            <input
              data-testid="transfer-amount"
              type="number"
              min="1"
              step="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="2,500"
              className={"mt-1 " + selectCls + " tabular-nums"}
            />
            {dollars > fromAvail && fromAvail > 0 && (
              <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-[#b23a48]">
                Exceeds available balance
              </p>
            )}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.22em] text-black/45">When</label>
            <select data-testid="transfer-when" className={"mt-1 " + selectCls} value={when} onChange={(e) => setWhen(e.target.value)}>
              <option value="asap">As soon as possible</option>
              <option value="next_business_day">Next business day</option>
              <option value="end_of_week">End of week</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-[10px] uppercase tracking-[0.22em] text-black/45">Note (optional)</label>
            <textarea
              data-testid="transfer-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Move idle checking cash into savings"
              className={"mt-1 " + selectCls + " resize-y"}
            />
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <button
            type="button"
            data-testid="transfer-submit"
            onClick={submit}
            disabled={!canSubmit || submitting}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#1e3d3a] text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-50 transition-colors duration-300"
          >
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRightLeft className="h-3.5 w-3.5" />}
            Request transfer
          </button>
          <button
            type="button"
            onClick={onClose}
            className="text-[11px] uppercase tracking-[0.22em] text-black/45 hover:text-[#1e3d3a] transition-colors duration-300 px-3"
          >
            Cancel
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function DiagnosticNudge({ onRequest, kind }) {
  const copy = kind === "recommendations"
    ? "Recommendations come from a full financial diagnostic. You're on Client Portal Access (self-service), so this stays empty until you ask us to review your numbers."
    : "Recovery progress and health scores come from a full financial diagnostic. You're on Client Portal Access (self-service) — keep tracking your own expenses, and reach out whenever you'd like us to dig in.";
  return (
    <div data-testid="diagnostic-nudge" className="mb-6 flex items-start gap-3 rounded-2xl border border-[#c9a961]/40 bg-[#1e3d3a] text-white px-5 py-4">
      <ClipboardList className="h-5 w-5 text-[#c9a961] shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="font-head text-lg leading-snug">Want a full diagnostic?</p>
        <p className="mt-1 text-[13px] text-white/70 leading-relaxed">{copy}</p>
        <button data-testid="diagnostic-nudge-cta" onClick={onRequest}
          className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#c9a961] px-4 py-2 text-[11px] uppercase tracking-[0.14em] text-[#1e3d3a] hover:bg-white transition-colors duration-300">
          <Sparkles className="h-3.5 w-3.5" /> Request a diagnostic
        </button>
      </div>
    </div>
  );
}

